import { map, filter } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance as axios, exportRequest } from '../../utils/axios';
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  data: [],
  meta: null,
  params: {
    page: 1,
    active: true,
    deleted: false,
  },
  record: null,
  settings: null,
};

const slice = createSlice({
  name: 'hrAttendance',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getRecordsSuccess(state, action) {
      state.isLoading = false;
      state.data = action.payload.results;
      state.meta = action.payload.meta;
      state.error = false;
    },
    getRecordSuccess(state, action) {
      state.isLoading = false;
      state.record = action.payload;
      state.error = false;
    },
    createRecordSuccess(state, action) {
      state.isLoading = false;
      state.data = [...state.data, action.payload];
      state.error = false;
    },
    updateRecordSuccess(state, action) {
      state.record = action.payload;
      state.isLoading = false;
      state.data = map(state.data, (_record) => (_record.id === action.payload.id ? action.payload : _record));
      state.params = { ...state.params };
      state.error = false;
    },
    deleteRecordSuccess(state, action) {
      state.isLoading = false;
      state.data = filter(state.data, (r) => r.id !== action.payload.id);
      state.error = false;
    },
    getSettingsSuccess(state, action) {
      state.isLoading = false;
      state.settings = action.payload;
      state.error = false;
    },
    setParams(state, action) {
      state.params = action.payload;
    },
    refreshParams(state) {
      state.params = { ...state.params };
    },
  },
});

export default slice.reducer;
export const { setParams, refreshParams } = slice.actions;

// ----------------------------------------------------------------------

export function getAttendanceRecords(params = {}) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/hr/attendance/', { params });
      dispatch(slice.actions.getRecordsSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function getAttendanceRecord(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/hr/attendance/${id}/`);
      dispatch(slice.actions.getRecordSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createAttendanceRecord(record) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/hr/attendance/', record);
      dispatch(slice.actions.createRecordSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateAttendanceRecord(id, record) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`/hr/attendance/${id}/`, record);
      dispatch(slice.actions.updateRecordSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function actionAttendanceRecord(id, data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/hr/attendance/${id}/action/`, data);
      dispatch(slice.actions.updateRecordSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function bulkCreateAttendance(data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/hr/attendance/bulk/', data);
      dispatch(slice.actions.refreshParams());
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getDailySummary(date) {
  return async () => {
    try {
      const response = await axios.get('/hr/attendance/daily-summary/', { params: { date } });
      return response.data;
    } catch (error) {
      return null;
    }
  };
}

export function getMonthlyGrid(year, month) {
  return async () => {
    try {
      const response = await axios.get('/hr/attendance/monthly-grid/', { params: { year, month } });
      return response.data;
    } catch (error) {
      return null;
    }
  };
}

export function getAttendanceSettings() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/hr/attendance-settings/');
      dispatch(slice.actions.getSettingsSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      return error;
    }
  };
}

export function updateAttendanceSettings(data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch('/hr/attendance-settings/update/', data);
      dispatch(slice.actions.getSettingsSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function exportAttendanceRecords(params = {}) {
  return async () => {
    exportRequest('/hr/attendance/export/', params, 'hr.Attendance');
  };
}
